import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// CPFs inválidos conhecidos da Receita Federal
const INVALID_CPFS = [
  '00000000000', '11111111111', '22222222222', '33333333333', '44444444444',
  '55555555555', '66666666666', '77777777777', '88888888888', '99999999999',
  '12345678909' // CPF inválido conhecido
];

export function isValidCPF(cpf: string): boolean {
  // Remove caracteres não numéricos
  cpf = cpf.replace(/[^\d]+/g, '');
  
  // Verifica se tem 11 dígitos
  if (cpf.length !== 11) return false;
  
  // Verifica se é um CPF inválido conhecido
  if (INVALID_CPFS.includes(cpf)) return false;
  
  // Verifica se todos os dígitos são iguais
  if (cpf.match(/(\d)\1{10}/)) return false;
  
  // Converte para array de números
  const digits = cpf.split('').map(el => +el);
  
  // Calcula o primeiro dígito verificador
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += digits[i] * (10 - i);
  }
  let remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== digits[9]) return false;
  
  // Calcula o segundo dígito verificador
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += digits[i] * (11 - i);
  }
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== digits[10]) return false;
  
  return true;
}

// Função para verificar se CPF já está cadastrado
export const checkCPFExists = async (
  cpf: string,
  userId: string,
  excludeId?: string
): Promise<{ exists: boolean; clientName?: string }> => {
  try {
    const { supabase } = await import('@/integrations/supabase/client');
    
    // Limpa o CPF removendo formatação
    const cleanCpf = cpf.replace(/[^\d]+/g, '');
    
    // Se o CPF não tem 11 dígitos, não faz a verificação
    if (cleanCpf.length !== 11) {
      return { exists: false };
    }
    
    let query = supabase
      .from('clients')
      .select('id, name, cpf_cnpj')
      .eq('user_id', userId)
      .not('cpf_cnpj', 'is', null); // Exclui registros onde cpf_cnpj é null
    
    if (excludeId) {
      query = query.neq('id', excludeId);
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error('Erro ao verificar CPF:', error);
      return { exists: false };
    }
    
    // Verifica se algum cliente tem o mesmo CPF (comparando números limpos)
    if (data && data.length > 0) {
      for (const client of data) {
        if (client.cpf_cnpj) {
          const clientCleanCpf = client.cpf_cnpj.replace(/[^\d]+/g, '');
          
          if (clientCleanCpf === cleanCpf) {
            return { 
              exists: true, 
              clientName: client.name 
            };
          }
        }
      }
    }
    
    return { exists: false };
  } catch (error) {
    console.error('Erro ao verificar CPF:', error);
    return { exists: false };
  }
};
